import React from 'react';
import { FormattedMessage } from 'react-intl';
import { PropTypes } from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { List, ListSubheader } from '@material-ui/core';
import { useMeContext } from '~/modules/me';
import SkillDetails from './SkillDetails';

export const useStyles = makeStyles(theme => ({
  description: {
    fontSize: theme.typography.fontSize,
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
    marginTop: theme.spacing(1)
  },
  title: {
    fontSize: theme.typography.fontSize,
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightBold
  },
  skillName: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1,
    padding: theme.spacing(1, 0, 1, 0),
    backgroundColor: theme.palette.background.paper
  },
  root: {
    borderBottom: `1px solid ${theme.palette.table.border}`
  }
}));

export const getSkillHeader = ({ classes }) => (
  <ListSubheader className={classes.skillName}>
    <div className={classes.title}>
      <FormattedMessage id="skillInfoCard.userDefinedSkills" />
    </div>
    <div className={classes.description}>
      <FormattedMessage id="skillInfoCard.skillsDescription" />
    </div>
  </ListSubheader>
);

const createSkillAssignment = suggestion => ({
  ...suggestion,
  id: null,
  uri: null,
  displayText: suggestion.skillName,
  skillLevel: {
    id: suggestion.skillLevelUri,
    uri: suggestion.skillLevelUri,
    displayText: suggestion.skillLevelName,
    rating: suggestion.skillLevel
  },
  certificate: null,
  skillCustomMetadata: [],
  isDelaRecommendation: true,
  isUserDefinedSkill: true,
  nudgeId: suggestion.nudgeId
});

const createCategory = suggestion => ({
  uri: suggestion.categoryUri,
  displayText: suggestion.categoryName
});

export const UserDefinedSkillList = ({
  userDefinedSkills,
  userDefinedSkillSuggestions,
  maxSkillLevel,
  certificates,
  canEditSkills,
  onRejectUserNudges,
  onAcceptUserDefinedSkills
}) => {
  const classes = useStyles();
  const {
    featureFlags: { isPsaPraaResourceMatchingEnabled }
  } = useMeContext();

  const hasUserDefinedSkills =
    userDefinedSkills && userDefinedSkills.length > 0;
  const hasUserDefinedSuggestions =
    isPsaPraaResourceMatchingEnabled &&
    userDefinedSkillSuggestions &&
    userDefinedSkillSuggestions.length > 0;
  const shouldRender =
    isPsaPraaResourceMatchingEnabled &&
    (hasUserDefinedSkills ||
      (isPsaPraaResourceMatchingEnabled && hasUserDefinedSuggestions));

  if (!shouldRender) {
    return null;
  }

  return (
    <div>
      <List subheader={getSkillHeader({ classes })}>
        {(userDefinedSkills || []).map(skill => (
          <SkillDetails
            key={skill.displayText}
            maxSkillLevel={maxSkillLevel}
            skillAssignment={skill}
            canEditSkills={canEditSkills}
          />
        ))}
        {isPsaPraaResourceMatchingEnabled &&
          userDefinedSkillSuggestions?.map(suggestion => {
            const skillAssignment = createSkillAssignment(suggestion);
            const category = createCategory(suggestion);

            return (
              <SkillDetails
                key={`suggestion-${suggestion.nudgeId}`}
                dataQeId={`UserDefinedSuggestion_${suggestion.nudgeId}`}
                maxSkillLevel={maxSkillLevel}
                skillAssignment={skillAssignment}
                certificates={certificates}
                category={category}
                canEditSkills={canEditSkills}
                onAcceptUserNudges={onAcceptUserDefinedSkills}
                onRejectUserNudges={onRejectUserNudges}
              />
            );
          })}
      </List>
    </div>
  );
};

UserDefinedSkillList.propTypes = {
  userDefinedSkills: PropTypes.array,
  userDefinedSkillSuggestions: PropTypes.array,
  maxSkillLevel: PropTypes.number,
  certificates: PropTypes.array,
  canEditSkills: PropTypes.bool,
  onRejectUserNudges: PropTypes.func,
  onAcceptUserDefinedSkills: PropTypes.func
};

export default UserDefinedSkillList;
